// THE VR LOADING VIEW (vr-beam-loading, owner 2026-11-02: "the VR experience must be perfect";
// APP_STARTUP "The VR loading view"). From the session's FIRST frame after a ⤳ Beam until the world
// fades in, the headset shows a calm room instead of black: a sky dome with a horizon, a floor, the
// destination's name and a progress ring driven by the start-up's own readiness (loadProgress).
//
// Comfort: every mesh is a child of the RIG — fixed in the tracking space, so the rig's moves (the
// placement's yaw, the VR landing's unseen descent, headFold) are never seen; the head is free (no
// head-locked motion); the panel turns toward the head only when it is looked away from (lazy, ≤
// FOLLOW_RAD_S); nothing blinks (the ring's highlight orbits smoothly). Cheap: 4 draws, ~2.6 k tris,
// one canvas texture written once per Beam, no per-frame upload. Drawn LAST over everything
// (renderOrder ORDER.., no depth test) and faded by one opacity; in frames it draws alone (main.js
// tick: a camera layer mask of XRLOAD_LAYER) the world is not drawn at all.
// THE PLACE (w59; xrrelief.js): once the body is placed the room shows the destination from the Map's own data — the
// skyline seen from the landing on the horizon, a table-top relief of the valley rising in front (setPlace) — and,
// under the VR look (vrstyle/), the room takes the look's colours (its void background on the horizon) so the world
// fades in without a colour jump. THE SETTLE (settleTerms, FpsWindow): the landing waits for the near world to be
// resident and the frame rate steady (main.js vrFlightStage), its share on the ring.
import * as THREE from 'three';
import { vrStyleOn, vrsBg } from './vrstyle/index.js';
import { placeSteps, placeMeshes, RELIEF_AHEAD, RELIEF_DROP, RELIEF_MIN_Y, RISE_MS } from './xrrelief.js';

export const XRLOAD_LAYER = 30;          // the loading view alone (main.js tick: the loading-only frames)
export const ORDER = 1990;               // over every world mesh, the vignette (999) and the card (1001)
export const ONLY_FRAMES = 2;            // the first frames a Beam shows draw the view alone (cheap: no world compile in them)
const PANEL_M = 2.4;                     // the panel's distance from the head's start point
const FOLLOW_START = 40 * Math.PI / 180; // the panel turns toward the head past this …
const FOLLOW_STOP = 3 * Math.PI / 180;   // … until within this
const FOLLOW_RAD_S = 1.2;                // at most this fast (eased)
const SPIN_S = 2.6;                      // the ring's highlight: one orbit per this many s
const STEP_MS = 2.5;                     // the place's build steps: at most this a frame (xrrelief.js placeSteps)
const FADE_IN = 0.35;                    // the place fades in over the first share of its rise

// THE SETTLE (main.js vrFlightStage, after the old VR landing's terms): the near world resident and the frame rate
// steady before the fade. s = { vis: [want, none] on-screen ground tiles and those with NOTHING resident under them (a
// hole; groundgen visState — finest-level misses are no signal: on the Quest ~80 % of the on-screen tiles draw a
// coarser fallback even in steady play, owner telemetry 2026-11-03 21:43) | null, gm: GROUND-M
// nodes queued + in flight, bld: building tiles within the void not drawn yet, trees: tree chunks queued, cq: compile
// jobs queued + running, fps: FpsWindow.read() }; c = the thresholds (flight vr_settle_*). → each term, ok, frac 0..1
export const SETTLE = { ground: 0, gm: 4, bld: 1, trees: 1, cq: 0, fps: 60, gap_ms: 30, win_ms: 1000 };
export function settleTerms(s, c = SETTLE) {
  const f = s.fps || { fps: 0, gap: Infinity, span: 0 };
  const T = {
    ground: !s.vis || s.vis[1] <= c.ground,
    gm: (s.gm ?? 0) <= c.gm, bld: (s.bld ?? 0) <= c.bld, trees: (s.trees ?? 0) <= c.trees, cq: (s.cq ?? 0) <= c.cq,
    fps: f.span >= 0.95 * c.win_ms && f.fps >= c.fps && f.gap <= c.gap_ms,
  };
  const keys = Object.keys(T), n = keys.filter((k) => T[k]).length;
  const fpsFrac = T.fps ? 1 : 0.9 * Math.max(0, Math.min(1, (f.span / c.win_ms) * Math.min(1, f.fps / c.fps)));
  return { ...T, ok: n === keys.length, frac: (n - (T.fps ? 1 : 0) + fpsFrac) / keys.length };
}
// the frame rate over the last win ms: push(now) every frame; read() = { fps, gap (the window's longest), span }
export class FpsWindow {
  constructor(winMs = SETTLE.win_ms) { this.win = winMs; this.t = []; }
  push(now) {
    this.t.push(now);
    while (this.t.length > 2 && now - this.t[1] > this.win) this.t.shift();
  }
  read() {
    const t = this.t, n = t.length;
    if (n < 2) return { fps: 0, gap: Infinity, span: 0 };
    let gap = 0;
    for (let i = 1; i < n; i++) gap = Math.max(gap, t[i] - t[i - 1]);
    const span = t[n - 1] - t[0];
    return { fps: span > 0 ? ((n - 1) * 1000) / span : 0, gap, span };
  }
}

// the room's colours: the VR look's (its void background on the horizon, vrstyle/index vrsBg) or the dusk room's
const hexRgb = (h) => [((h >> 16) & 255) / 255, ((h >> 8) & 255) / 255, (h & 255) / 255];
const mixv = (a, b, t) => a.map((x, i) => x + (b[i] - x) * t);
export function roomPalette(style = vrStyleOn(), bg = vrsBg()) {
  if (!style) return { style: 0, bg: [0.231, 0.333, 0.471], ridge: [0.05, 0.075, 0.11], floor: [0.40, 0.80, 0.90] };
  const b = hexRgb(bg), zen = [0.42, 0.60, 0.80];
  return { style: 1, bg: b, ridge: [0.36, 0.44, 0.51], floor: [0.16, 0.42, 0.50],
    zen, mid: mixv(zen, b, 0.6), hor: b, low: b.map((x) => x * 0.93), nad: b.map((x) => x * 0.82) };
}

// the readiness 0..1 (monotone: never back; 1 only when ready). s = {
//   placed: the server seated the body; latch: {hf, col, terrain, vt (0..1 of the near VT slots)} | true once latched;
//   terms: {top, spr, acts, veg, warm} (booleans), warmFrac 0..1 (the warm-up set's share done), test: readiness 0..1,
//   settle: the settle's share 0..1 (settleTerms frac), ready: the world shows now }
export function loadProgress(s, prev = 0) {
  if (s.ready) return 1;
  let now;
  if (!s.placed) now = 0.02;
  else if (s.latch !== true) {
    const L = s.latch || {};
    now = 0.05 + 0.4 * ((L.hf ? 1 : 0) + (L.col ? 1 : 0) + (L.terrain ? 1 : 0) + Math.max(0, Math.min(1, L.vt || 0))) / 4;
  } else {
    const T = s.terms || {};
    const warm = T.warm ? 1 : Math.max(0, Math.min(1, s.warmFrac || 0));
    now = 0.45 + 0.3 * ((T.top ? 1 : 0) + (T.spr ? 1 : 0) + (T.acts ? 1 : 0) + (T.veg ? 1 : 0) + warm + Math.max(0, Math.min(1, s.test || 0))) / 6
      + 0.24 * Math.max(0, Math.min(1, s.settle || 0));
  }
  return Math.min(0.99, Math.max(prev, now));
}

// the view's target opacity this frame (main.js tick): presenting + entering (the session runs
// before the place reply) → 1; in the world → 1 until the VR landing has been seen (fl.seen),
// its fade 1 → 0 over fadeMs, 0 after; the instant wait: 1 until the release (_beamIn gone)
export function loadOpacity({ presenting, mode, entering, beamIn, fl, now, fadeMs }) {
  if (!presenting) return 0;
  if (!mode) return entering ? 1 : 0;
  if (mode !== 'vr' || !beamIn) return 0;
  if (!fl || !fl.seen) return 1;
  return fl.stage === 'fade' ? 1 - Math.min(1, (now - fl.tFade) / fadeMs) : 0;
}

// one step of the shown opacity: up at once, down never faster than the fade (the instant wait's
// release would drop it in one frame)
export function stepOpacity(o, target, dtMs, fadeMs) {
  return target >= o ? target : Math.max(target, o - dtMs / Math.max(1, fadeMs));
}

// the panel's yaw one step toward the head's (lazy follow): idle within FOLLOW_START, then eased
// toward it at ≤ FOLLOW_RAD_S until within FOLLOW_STOP
export function followYaw(cur, head, following, dt) {
  const d = Math.atan2(Math.sin(head - cur), Math.cos(head - cur));
  if (!following && Math.abs(d) < FOLLOW_START) return { yaw: cur, following: false };
  if (Math.abs(d) <= FOLLOW_STOP) return { yaw: cur, following: false };
  const step = Math.sign(d) * Math.min(Math.abs(d) * (1 - Math.exp(-dt * 2.5)), FOLLOW_RAD_S * dt);
  return { yaw: cur + step, following: true };
}

const BASE = { transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false };
const VS = 'varying vec3 vP; varying vec2 vUv; void main() { vP = position; vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }';
// sRGB display values out as they are (no tone map / colour-space chunk): the look is this file's
const DOME_FS = `uniform float uO, uSt; uniform vec3 sZen, sMid, sHor, sLow, sNad; varying vec3 vP;
float h(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
void main() {
  float y = normalize(vP).y;
  vec3 zen = vec3(0.039, 0.071, 0.133), mid = vec3(0.086, 0.141, 0.235), hor = vec3(0.231, 0.333, 0.471);
  vec3 low = vec3(0.071, 0.102, 0.149), nad = vec3(0.043, 0.059, 0.086);
  vec3 c = y > 0.0 ? mix(hor, mix(mid, zen, smoothstep(0.25, 1.0, y)), smoothstep(0.0, 0.25, y)) : mix(low, nad, smoothstep(0.0, 0.6, -y));
  c += vec3(0.40, 0.75, 0.85) * 0.22 * exp(-abs(y) * 70.0);
  vec3 s = y > 0.0 ? mix(sHor, mix(sMid, sZen, smoothstep(0.25, 1.0, y)), smoothstep(0.0, 0.25, y)) : mix(sLow, sNad, smoothstep(0.0, 0.6, -y));
  c = mix(c, s, uSt);
  c += (h(gl_FragCoord.xy) - 0.5) / 255.0;
  gl_FragColor = vec4(c, uO);
}`;
const FLOOR_FS = `uniform float uO; uniform vec3 uFc; varying vec3 vP;
void main() {
  float r = length(vP.xy), w = fwidth(r);
  float ring = 1.0 - smoothstep(0.0, 1.5 * w + 0.004, abs(fract(r + 0.5) - 0.5));
  float a = (0.10 + 0.25 * ring) * (1.0 - smoothstep(1.5, 6.0, r));
  gl_FragColor = vec4(uFc, a * uO);
}`;
const PANEL_FS = `uniform float uO; uniform sampler2D map; varying vec2 vUv;
void main() { vec4 t = texture2D(map, vUv); gl_FragColor = vec4(t.rgb, t.a * uO); }`;
const RING_FS = `uniform float uO, uP, uSpin, uR0, uR1; varying vec3 vP;
void main() {
  float r = length(vP.xy), w = fwidth(r);
  float band = smoothstep(uR0, uR0 + w, r) * (1.0 - smoothstep(uR1 - w, uR1, r));
  float f = fract(atan(vP.x, vP.y) / 6.2831853 + 1.0);
  float fw = fwidth(f) < 0.5 ? fwidth(f) : 0.0;
  float done = 1.0 - smoothstep(uP - fw, uP + fw, f);
  float d = fract(f - uSpin + 0.5) - 0.5;
  float glow = exp(-d * d / 0.004);
  vec3 track = vec3(0.157, 0.204, 0.282) + vec3(0.30, 0.55, 0.62) * 0.45 * glow;
  vec3 c = mix(track, vec3(0.404, 0.910, 0.976), done);
  gl_FragColor = vec4(c, band * mix(0.85, 1.0, done) * uO);
}`;

export class XrLoad {
  constructor({ rig }) {
    this.rig = rig;
    const g = this.group = new THREE.Group();
    g.name = 'xrload';
    g.visible = false;
    this.u = { uO: { value: 0 } };
    const mat = (fs, extra = {}, side = THREE.FrontSide) => new THREE.ShaderMaterial({ ...BASE, side, vertexShader: VS, fragmentShader: fs,
      uniforms: { uO: this.u.uO, ...extra } });
    const mesh = (geo, m, order) => {
      const o = new THREE.Mesh(geo, m);
      o.renderOrder = order; o.frustumCulled = false; o.layers.enable(XRLOAD_LAYER);
      return o;
    };
    const P = this.pal = roomPalette();
    const v3 = (a, d) => ({ value: new THREE.Vector3(...(a || d)) });
    this.dome = mesh(new THREE.SphereGeometry(40, 48, 24), mat(DOME_FS, { uSt: { value: P.style }, sZen: v3(P.zen, P.bg), sMid: v3(P.mid, P.bg),
      sHor: v3(P.hor, P.bg), sLow: v3(P.low, P.bg), sNad: v3(P.nad, P.bg) }, THREE.BackSide), ORDER);
    const fg = new THREE.CircleGeometry(6, 96);
    this.floor = mesh(fg, mat(FLOOR_FS, { uFc: v3(P.floor) }), ORDER + 1);
    this.floor.rotation.x = -Math.PI / 2;
    this.floor.position.y = 0.002;
    // the panel: name + world + status (a canvas texture, written once per Beam) and the ring above it
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1024; this.canvas.height = 320;
    this.tex = new THREE.CanvasTexture(this.canvas);
    this.tex.colorSpace = THREE.NoColorSpace;     // raw sRGB bytes out (PANEL_FS)
    this.tex.anisotropy = 4;
    this.panel = new THREE.Group();
    this.text = mesh(new THREE.PlaneGeometry(1.4, 0.4375), mat(PANEL_FS, { map: { value: this.tex } }), ORDER + 2);
    this.ringU = { uP: { value: 0 }, uSpin: { value: 0 }, uR0: { value: 0.112 }, uR1: { value: 0.136 } };
    this.ring = mesh(new THREE.RingGeometry(0.1, 0.15, 128, 1), mat(RING_FS, this.ringU), ORDER + 3);
    this.ring.position.y = 0.36;
    this.panel.add(this.text, this.ring);
    this.yawG = new THREE.Group();
    this.yawG.add(this.panel);
    this.panel.position.z = -PANEL_M;
    g.add(this.dome, this.floor, this.yawG);
    rig.add(g);
    this.o = 0; this.p = 0; this.pShow = 0; this.drawn = 0; this.placed = false; this.following = false;
    this.at = -Infinity; this.label = '';
    this.head0 = null;            // the head where the room was placed (rig space)
    this._job = null; this._pl = null; this._plAt = 0; this._plMs = 0; this._plWhy = null;
  }
  // THE PLACE (xrrelief.js): the Map's data for the landing (d = placeSteps' input; bg / ridge from the room's
  // palette) — built in steps from the next frames, then shown: the skyline around the head, the relief rising ahead
  setPlace(d) {
    this._drop();
    this._job = placeSteps({ ...d, bg: this.pal.bg, ridge: this.pal.ridge });
    this._plMs = 0; this._plWhy = null;
  }
  _drop() {
    if (this._pl) { this._pl.dispose(); this._pl = null; }
    this._job = null;
  }
  // the place's steps (≤ STEP_MS) and pose this frame: north-aligned (the rig's yaw undone), the sky at the head's
  // start, the table ahead of it rising over RISE_MS, faded in with it
  _place(now) {
    if (this._job && this.head0) {
      const t0 = performance.now();
      try {
        let r;
        do { r = this._job.next(); } while (!r.done && performance.now() - t0 < STEP_MS);
        this._plMs += performance.now() - t0;
        if (r.done) {
          this._job = null;
          this._pl = placeMeshes(r.value, { order: ORDER, layer: XRLOAD_LAYER });
          for (const o of [this._pl.sky, this._pl.table]) if (o) this.group.add(o);
          this._plAt = now;
          this._plWhy = r.value.relief ? (r.value.picture ? 'map' : 'heights') : 'none';
        }
      } catch (e) { this._job = null; this._plWhy = 'error'; console.warn('[xrload] place:', e); }
    }
    const P = this._pl;
    if (!P) return;
    const h = this.head0, yaw = this.rig ? -this.rig.rotation.y : 0;
    const k = Math.min(1, (now - this._plAt) / RISE_MS), e = 1 - Math.pow(1 - k, 3);
    P.uR.value = this.o * Math.min(1, k / FADE_IN);
    if (P.sky) { P.sky.position.set(h.x, h.y, h.z); P.sky.rotation.y = yaw; }
    if (P.table) {
      const top = Math.max(RELIEF_MIN_Y, h.y - RELIEF_DROP);
      P.table.position.set(h.x - Math.sin(h.yaw) * RELIEF_AHEAD, top * e, h.z - Math.cos(h.yaw) * RELIEF_AHEAD);
      P.table.rotation.y = yaw;
    }
  }
  // a Beam: the name on the panel, the progress from 0; it shows once a session presents
  show(label, world, status) {
    this.label = label || world || '';
    this.p = 0; this.pShow = 0; this.drawn = 0; this.placed = false; this.following = false;
    this._drop(); this.head0 = null;
    const c = this.canvas, x = c.getContext('2d');
    x.clearRect(0, 0, c.width, c.height);
    x.fillStyle = 'rgba(11,14,20,0.55)';
    x.beginPath();
    x.roundRect(8, 8, c.width - 16, c.height - 16, 36);
    x.fill();
    x.textAlign = 'center'; x.textBaseline = 'middle';
    const fit = (s, px, weight) => {
      let f = px;
      do { x.font = `${weight} ${f}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`; f -= 2; } while (f > 20 && x.measureText(s).width > c.width - 80);
    };
    const name = label || world || '';
    fit(name, 84, 700); x.fillStyle = '#e8ecf4'; x.fillText(name, c.width / 2, 108);
    const second = label && world && label !== world ? world : '';
    if (second) { fit(second, 40, 500); x.fillStyle = '#9ab4d6'; x.fillText(second, c.width / 2, 186); }
    fit(status, 38, 600); x.fillStyle = '#67e8f9'; x.fillText(status, c.width / 2, second ? 252 : 212);
    this.tex.needsUpdate = true;
  }
  // per frame (main.js tick, before the draw): target opacity, the readiness (loadProgress),
  // the session's frame for the head (rig space = the reference space). Returns the opacity drawn
  update(now, xr, target, progress, fadeMs) {
    const dt = this.at === -Infinity ? 0 : Math.min(0.1, (now - this.at) / 1000);
    this.at = now;
    this.o = stepOpacity(this.o, target, dt * 1000, fadeMs);
    const on = this.o > 0.001;
    this.group.visible = on;
    this.u.uO.value = this.o;
    if (!on) { this.drawn = 0; if (this._pl || this._job) this._drop(); return 0; }
    this.drawn++;
    this.p = Math.max(this.p, progress);
    this.pShow += (this.p - this.pShow) * (1 - Math.exp(-dt * 3));
    if (this.p >= 1 && this.pShow > 0.995) this.pShow = 1;
    this.ringU.uP.value = this.pShow;
    this.ringU.uSpin.value = (now / 1000 / SPIN_S) % 1;
    const head = this.head(xr);
    if (head) {
      if (!this.placed) {
        // the room around the head where it stands: the dome's centre, the panel ahead at eye height
        this.placed = true;
        this.dome.position.set(head.x, head.y, head.z);
        this.yawG.position.set(head.x, 0, head.z);
        this.panel.position.y = Math.max(1.0, Math.min(1.9, head.y - 0.05));
        this.yawG.rotation.y = head.yaw;
        this.head0 = head;
      } else {
        const f = followYaw(this.yawG.rotation.y, head.yaw, this.following, dt);
        this.yawG.rotation.y = f.yaw; this.following = f.following;
      }
    }
    this._place(now);
    return this.o;
  }
  // the head in rig space: the viewer pose of this XR frame in the reference space (null: none)
  head(xr) {
    const fr = xr && xr.getFrame && xr.getFrame(), ref = xr && xr.getReferenceSpace && xr.getReferenceSpace();
    const vp = fr && ref ? fr.getViewerPose(ref) : null;
    if (!vp) return null;
    const p = vp.transform.position, q = vp.transform.orientation;
    // yaw of the view direction (-Z rotated by q) about +Y
    const fx = -2 * (q.x * q.z + q.w * q.y), fz = -(1 - 2 * (q.x * q.x + q.y * q.y));
    return { x: p.x, y: p.y, z: p.z, yaw: Math.atan2(-fx, -fz) };
  }
  // this frame draws the loading view ALONE (no world): presenting, fully opaque, and either no
  // world yet (the session before the place reply) or one of the first ONLY_FRAMES of the view
  only(presenting, mode) {
    return presenting && this.o >= 1 && (!mode || this.drawn <= ONLY_FRAMES);
  }
  state() {
    return { o: +this.o.toFixed(3), p: +this.p.toFixed(3), shown: +this.pShow.toFixed(3), drawn: this.drawn, placed: this.placed, label: this.label,
      place: this._pl ? { why: this._plWhy, sky: !!this._pl.sky, relief: !!this._pl.table, ms: +this._plMs.toFixed(1), rise: +this._pl.uR.value.toFixed(3) }
        : this._job ? 'building' : this._plWhy, style: this.pal.style };
  }
}
